/**
 * useCopy() — typed wrapper over react-i18next.
 *
 * Resolution order:
 *   1. Active-locale catalog entry.
 *   2. en catalog entry (i18next fallback chain).
 *   3. The explicit `fallback` argument.
 *
 * Brand Center's own chrome is not white-labeled (it edits brands rather than wearing one),
 * so there is no per-tenant `copyStrings` override layer here.
 */
import { useTranslation } from 'react-i18next';
import '@/i18n/i18n';

interface InterpolationValues {
  [k: string]: string | number;
}

export function useCopy(): {
  t: (key: string, fallback?: string, values?: InterpolationValues) => string;
} {
  const { t, i18n } = useTranslation();
  return {
    t: (key, fallback, values) => {
      const exists = i18n.exists(key);
      if (exists) {
        const translated = t(key, values ?? {});
        if (typeof translated === 'string' && translated.length > 0 && translated !== key) {
          return translated;
        }
      }
      return interpolate(fallback ?? key, values);
    },
  };
}

function interpolate(template: string, values: InterpolationValues | undefined): string {
  if (!values) return template;
  return template.replace(/\{\{(\w+)\}\}/g, (_, name: string) => {
    const v = values[name];
    return v === undefined ? `{{${name}}}` : String(v);
  });
}
